import { CRUMBLE, LADYBIRD } from '@/game/config';

/**
 * Pure decision logic for the Crumbling Board slow-play penalty (specs/crumbling-board.md).
 *
 * Kept free of Phaser so it can be unit-tested directly (like wordPlacement / audio). BoardScene
 * and RoundScene call into these; the animation and life-loss wiring live in the scenes.
 */

/** Board-clear times the PACE (the centre) is measured over. Three is enough to outvote a fluke,
 *  short enough to still follow a child warming up or tiring. */
export const PACE_WINDOW = 3;

/**
 * Board-clear times the SPREAD is measured over. Longer than the pace window on purpose: estimating
 * how much a player varies needs more samples than estimating where they sit, and the spread should
 * not lurch about from board to board the way a 3-sample estimate would.
 */
export const SPREAD_WINDOW = 8;

/**
 * Cleared boards required before the mechanic activates at all.
 *
 * A spread estimated from one or two boards is meaningless, and a degenerate one would set an absurd
 * threshold on board 2. Below this the clock stays null — the same "inactive until we know
 * something" stance the mechanic always had on board 1, just extended to cover the new estimate.
 * The side effect is a short unpressured warm-up at the start of every game, which is the right end
 * of a session to be generous at.
 */
export const MIN_SPREAD_SAMPLES = 4;

/**
 * The most recent board-clear times (ms), newest first, capped at SPREAD_WINDOW.
 *
 * This replaced a running MINIMUM, which was a ratchet: it only ever went down, so a single lucky
 * fast board set the clock for the whole session and no amount of slower play could loosen it
 * again. A child who happened to clear one board in three seconds was held to that for the next
 * twenty minutes.
 */
export function pushBoardTime(prev: readonly number[], elapsedMs: number): number[] {
  return [elapsedMs, ...prev].slice(0, SPREAD_WINDOW);
}

/** Median of a sample. Undefined behaviour on an empty array — callers guard. */
function medianOf(values: readonly number[]): number {
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 1 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

/**
 * The centre the crumble threshold is measured from: the MEDIAN of the most recent PACE_WINDOW
 * boards, or null until a board has been cleared.
 *
 * Median rather than mean, deliberately. One 3s board among 8s and 9s barely moves a median but
 * drags a mean down by nearly two seconds — and it is exactly that outlier the old rule punished
 * children for. Fluke-resistance is the whole point of the window, so the summary has to be
 * fluke-resistant too.
 */
export function pace(recent: readonly number[]): number | null {
  if (recent.length === 0) return null;
  return medianOf(recent.slice(0, PACE_WINDOW));
}

/**
 * How much this player varies — a robust standard deviation of their board times **in log space**,
 * over the last SPREAD_WINDOW boards. Dimensionless (it is a relative spread, not milliseconds), and
 * null until there are MIN_SPREAD_SAMPLES boards to estimate from.
 *
 * Log space is not decoration, it is what makes the rule fair. Board times are multiplicative: a
 * player who is twice as slow is also twice as scattered in absolute ms. Measured in log units that
 * cancels, so `spread` means "how erratic is this player" on a scale every player shares, and `k`
 * below can be a plain number of standard deviations with the same meaning for everyone. An additive
 * `pace + k × spread_in_ms` rule was tried first and does NOT equalise — the log curvature leaves a
 * large residual gap between a steady player and a scattered one.
 *
 * Median absolute deviation scaled by 1.4826 (the constant that makes MAD agree with the standard
 * deviation on normal data). Robust rather than a plain standard deviation because a single
 * attention lapse would otherwise dominate the estimate — and lapses are ordinary behaviour for the
 * players this rule exists to protect, not outliers to be amplified.
 *
 * This is what lets the threshold be fair across ages without ever asking one: a first-grader
 * announces themselves through a large spread within a handful of boards, an adult through a tiny
 * one. See specs/adaptive-difficulty.md §1.2.
 */
export function spreadRatio(recent: readonly number[]): number | null {
  if (recent.length < MIN_SPREAD_SAMPLES) return null;
  const logs = recent.slice(0, SPREAD_WINDOW).filter((t) => t > 0).map((t) => Math.log(t));
  if (logs.length < MIN_SPREAD_SAMPLES) return null;
  const centre = medianOf(logs);
  return medianOf(logs.map((v) => Math.abs(v - centre))) * 1.4826;
}

/**
 * The allowance actually granted, after the CONSISTENCY PRESS: below `CRUMBLE.spreadKnee` the
 * player's own `k` is scaled down, linearly from no reduction at the knee to
 * `1 − CRUMBLE.consistencyPress` at zero spread.
 *
 * This is the one place the mechanic deliberately stops equalising. `spreadK` on its own gives every
 * player the same headroom measured in their own deviations (§1), which is the floor the design
 * should sit on — but for a metronomic player `exp(k × spread)` is barely above 1 whatever `k` is,
 * so equal headroom still means an unpressed game. Measured, the crumble fired on 12% of an adult's
 * boards and cost them 0.001 hearts a board. The knee asks more of a player who has demonstrably
 * got the measure of the game, and asks it in the one currency that separates the cohorts *and*
 * cannot be crossed by playing longer or better: how consistent you are.
 *
 * Why the ramp is linear in spread and multiplies `k` rather than subtracting from it:
 *  - MULTIPLIES, so it composes with the two things that already scale `k` — the per-mode value
 *    (2.4 / 3.0 / 3.0) and `timeoutMultFor(sessionStars)` — instead of quietly meaning something
 *    different in each mode.
 *  - MONOTONIC: the exponent becomes `k(1−p)·s + k·p·s²/knee`, which is increasing in `s`. A more
 *    scattered player can therefore never be handed a tighter clock than a steadier one, which
 *    would have reinstated the exact defect §1 removed. Continuous at the knee, so there is no step
 *    a player can feel themselves cross.
 *
 * See specs/adaptive-difficulty.md §6.
 */
export function pressedK(k: number, spread: number): number {
  const knee = CRUMBLE.spreadKnee;
  if (!(knee > 0) || spread >= knee) return k;
  const press = Math.min(1, Math.max(0, CRUMBLE.consistencyPress));
  return k * (1 - press * (1 - Math.max(0, spread) / knee));
}

/**
 * The elapsed board time at which the current board begins to crumble, or `null` while the mechanic
 * is inactive (fewer than MIN_SPREAD_SAMPLES cleared boards this game).
 *
 *   threshold = max( pace × exp(pressedK(k, spread) × spread) , minMs )
 *
 * `k` is how many of the player's OWN standard deviations a board may run over their own typical
 * time. Because `spread` is measured in log space, that has the same meaning for everyone: a board
 * crumbles at roughly the same tail probability whether the player is a metronomic adult or an
 * erratic six-year-old. Both terms come from that player's recent boards, so the rule adapts to
 * whoever is holding the device without being told anything about them.
 *
 * See specs/adaptive-difficulty.md §1.
 */
export function crumbleThresholdMs(
  pace: number | null,
  spread: number | null,
  k: number = CRUMBLE.spreadK,
  minMs = 0,
  /**
   * Flat multiplier on the finished clock — `clockTrimFor(sessionStars)` in the scenes. Applied
   * AFTER the exponent and BEFORE the floor, which is the only place a change can mean "x% less
   * time" for every player alike: inside the exponent it would be worth `exp(Δk × spread)`, i.e. a
   * different amount for everyone and least for the steadiest. See src/game/level.ts.
   */
  trim = 1,
): number | null {
  // `null` still means "no crumble on this board" — more lenient than any floor, so the floor only
  // ever raises a threshold that already exists.
  if (pace === null || spread === null) return null;
  // Guard against a degenerate estimate (a handful of boards that happened to land almost on top of
  // each other) collapsing the threshold onto the pace itself. Deliberately well BELOW any real
  // player's spread — an adult measures ~0.09 — so it is a safety net and not a policy lever. An
  // earlier value of 0.20 sat above the adult spread and bound on every single adult board, which
  // quietly undid the whole point of the rule.
  const effective = Math.max(spread, CRUMBLE.minSpreadRatio);
  // The mode floor is applied last, so a trim can never take a beginning reader's minimum reading
  // time away — it can only spend the headroom the pace-and-spread rule created above it.
  return Math.max(pace * Math.exp(pressedK(k, effective) * effective) * trim, minMs);
}

/**
 * How long one word stays shootable once it has started to fall — the "you can race the collapse"
 * window, stretched to the player's own reading speed.
 *
 * `pace / tiles` is their seconds-per-tile, from the same window the threshold uses. Floored at
 * `CRUMBLE.fallMs + cornerGapMs` so nobody gets less time than they did before, and capped so a very
 * slow pace cannot leave a word hanging indefinitely. See specs/adaptive-difficulty.md §2.
 */
export function rescueWindowMs(paceMs: number | null, tiles: number): number {
  const floor = CRUMBLE.cornerGapMs + CRUMBLE.fallMs;
  if (paceMs === null || !Number.isFinite(paceMs) || paceMs <= 0 || tiles <= 0) return floor;
  const perTile = paceMs / tiles;
  return Math.min(CRUMBLE.rescueMaxMs, Math.max(floor, CRUMBLE.rescuePerWord * perTile));
}

/** The fall duration for one word: the rescue window less the fixed tilt beat before it. */
export function wordFallMs(paceMs: number | null, tiles: number): number {
  return rescueWindowMs(paceMs, tiles) - CRUMBLE.cornerGapMs;
}

/** Whether the board should be crumbling, given elapsed board time and the threshold. */
export function shouldCrumble(elapsedMs: number, thresholdMs: number | null): boolean {
  return thresholdMs !== null && elapsedMs > thresholdMs;
}

/** What happens when a plank falls off the bottom of the screen. */
export interface DropOutcome {
  /** Costs one life — only target (rule-matching) words do. Decoys falling is cosmetic. */
  loseLife: boolean;
  /** Counts toward board completion (decrements remainingTargets) — targets resolve by loss, so a
   *  fallen target can never soft-lock the board. */
  resolvesTarget: boolean;
}

export function dropOutcome(isTarget: boolean): DropOutcome {
  return { loseLife: isTarget, resolvesTarget: isTarget };
}

/**
 * Deterministic collapse order for a set of laid-out planks: reading order — top-to-bottom, then
 * left-to-right — so the board reads as sagging from the top. Returns indices into `positions`.
 * Determinism matters for testability and reproducible feel.
 */
export function cascadeOrder(positions: ReadonlyArray<{ x: number; y: number }>): number[] {
  return positions
    .map((p, i) => ({ p, i }))
    .sort((a, b) => a.p.y - b.p.y || a.p.x - b.p.x || a.i - b.i)
    .map((e) => e.i);
}

/**
 * Which slow-play timeout action to run. `roll` is a value in [0, 1) (e.g. Math.random()); when it
 * falls under `ladybirdChance` the board sends in the ladybird, otherwise it crumbles. Kept pure so
 * the 20/80 split is unit-testable without spawning anything.
 */
export function timeoutAction(
  roll: number,
  ladybirdChance: number = LADYBIRD.chance,
): 'ladybird' | 'crumble' {
  return roll < ladybirdChance ? 'ladybird' : 'crumble';
}

/**
 * Index of the position nearest to `from` (squared Euclidean distance). The ladybird uses this to
 * pick its next word to crawl to. Ties break on the lower index for determinism. Returns -1 for an
 * empty list.
 */
export function nearestIndex(
  from: { x: number; y: number },
  positions: ReadonlyArray<{ x: number; y: number }>,
): number {
  let best = -1;
  let bestD = Infinity;
  positions.forEach((p, i) => {
    const dx = p.x - from.x;
    const dy = p.y - from.y;
    const d = dx * dx + dy * dy;
    if (d < bestD) {
      bestD = d;
      best = i;
    }
  });
  return best;
}

/** Number of bites to eat a word — one per user-perceived character (code-point safe, so a
 *  multi-byte glyph like an emoji or a CJK character counts as one bite, not several). */
export function biteCount(text: string): number {
  return Array.from(text).length;
}
